import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import * as Act from './inventory.actions';
import {IRecord} from '../../interfaces/record.interface';
import {endpoints} from '../../constants/endpoints';
import {AuthState} from '../auth/auth.state';
import {IInventory, IInventoryStatusChange} from '../../interfaces/inventory.interface';
import { INDC } from '../../interfaces/INDC.interface';
import { ILibertyRxLoadedInventory } from '../../interfaces/ILibertyRxLoadedInventory';
import { IInventoryOnHand } from '../../interfaces/IInventoryOnHand';
import { ILibertyRxLoadInventoryStatus, LibertyRxLoadInventoryStatusValues } from '../../interfaces/ILibertyRxLoadInventoryStatus';
import { IResponse } from '../../interfaces/IResponse';
import { PharmacyState, PharmacyStateModel } from '../pharmacy/pharmacy.state';
import { DrugScheduleEnum } from '../../interfaces/DrugScheduleEnum ';
import { ITransactionModel } from '../../interfaces/ITransactionModel';
import { TransactionsService } from '../../services/transactions.service';
import { IInventoryLocation } from '../../interfaces/inventory-location.interface';
import { InventoryLocationTypeEnum } from '../../interfaces/InventoryLocationTypeEnum';
import { InventoryLocationStatusEnum } from '../../interfaces/InventoryLocationStatusEnum';
import { ICurrentInventoryOnLocation } from '../../interfaces/ICurrentInventoryOnLocation';
import { IInventoryModel } from '../../interfaces/IInventoryModel';
import { DrugsService } from '../../services/drugs.service';
import { ISimilarOnHand } from '../../interfaces/similar-on-hand.interface';

export interface InventoryStateModel {
  response: IResponse;
  loading: boolean;
  success: boolean;
  currentRecord: IRecord;
  inventoryList: IInventoryModel[];
  activeInventoryList: IInventory[];
  archivedInventoryList: IInventory[];
  currentInventory: IInventory;
  archivingStatus: IInventoryStatusChange;
  receiveSelectedDrugInventory: IInventory;
  // the inventory fetched from LibertyRx
  libertyRxLoadedInventory: INDC[];
  // the inventory fetched from C2Keep
  inventoryOnHandValues: IInventoryOnHand[];
  // determines if the pill loading spinner is shown
  loadingLibertyRxInventory: boolean;
  // determines if the poller is on
  libertyRxPolling: boolean;
  // determines if the "Configuration Settings Required" dialog is shown
  displayNoLibertySettingPopover: boolean;
  // determines if the "No New NDCs" dialog is shown
  displayNoNewNdcPopover: boolean;
  libertyRxLoadInventoryStatus: ILibertyRxLoadInventoryStatus;
  libertyRxLoadInventoryPercentage: number;

  // determines if the "Error" dialog is shown
  displayLoadLibertyRxInventoryErrorModal: boolean;
  // determines if the "Cancel" dialog is shown
  displayLoadLibertyRxInventoryCancelModal: boolean; 
  currentInventoryTransactions: ITransactionModel[];
  currentFilteredInventoryTransactions: ITransactionModel[];
  isLibertyRxIntegrationConfigurationOn: boolean;
  locations: IInventoryLocation[];
  currentInventoryForNdcOnLocation: ICurrentInventoryOnLocation;
  bulkInventoryForNdcOnLocation: ICurrentInventoryOnLocation[];
  similarOnHand: ISimilarOnHand[]
}  

@State<InventoryStateModel>({
  name: 'inventory',
})

@Injectable()
export class InventoryState {

  constructor(
    private http: HttpClient,
    private store: Store,
    private transactionService: TransactionsService,
    private drugService: DrugsService
  ) {
  }

  @Selector()
    static response(state: InventoryStateModel): IResponse {
      return state.response;
  }

  @Selector()
  static loading(state: InventoryStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: InventoryStateModel): boolean {
    return state.success;
  }

  @Selector()
  static currentRecord(state: InventoryStateModel): IRecord {
    return state.currentRecord;
  }

  @Selector()
  static currentInventory(state: InventoryStateModel): IInventory {
    return state.currentInventory;
  }

  @Selector()
  static inventoryList(state: InventoryStateModel): IInventoryModel[] {
    return state.inventoryList;
  }

  @Selector()
  static inventoryActiveList(state: InventoryStateModel): IInventory[] | null {
    return state.activeInventoryList;
  }

  @Selector()
  static inventoryArchivedList(state: InventoryStateModel): IInventory[] | null {
    return state.archivedInventoryList;
  }

  @Selector()
  static receiveSelectedDrugInventory(state: InventoryStateModel): IInventory | null {
    return state.receiveSelectedDrugInventory;
  }

  @Selector()
  static loadingLibertyRxInventory(state: InventoryStateModel): boolean{
    return state.loadingLibertyRxInventory;
  }

  @Selector()
  static libertyRxPolling(state: InventoryStateModel): boolean{
    return state.libertyRxPolling;
  }

  @Selector()
  static libertyRxLoadedInventory(state: InventoryStateModel): INDC[] | [] {
    return state.libertyRxLoadedInventory;
  }

  @Selector()
  static displayNoLibertySettingPopover(state: InventoryStateModel): boolean {
    return state.displayNoLibertySettingPopover;
  }

  @Selector()
  static displayNoNewNdcPopover(state: InventoryStateModel): boolean {
    return state.displayNoNewNdcPopover;
  }

  @Selector()
  static libertyRxLoadInventoryPercentage(state: InventoryStateModel): number {
    return state.libertyRxLoadInventoryPercentage;
  }

  @Selector()
  static libertyRxLoadInventoryStatus(state: InventoryStateModel): ILibertyRxLoadInventoryStatus {
    return state.libertyRxLoadInventoryStatus;
  }

  @Selector()
  static displayLoadLibertyRxInventoryErrorModal(state: InventoryStateModel): boolean {
    return state.displayLoadLibertyRxInventoryErrorModal;
  }

  @Selector()
  static displayLoadLibertyRxInventoryCancelModal(state: InventoryStateModel): boolean {
    return state.displayLoadLibertyRxInventoryCancelModal;
  }

  @Selector()
  static currentInventoryOnHand(state: InventoryStateModel): IInventoryOnHand[] {
    return state.inventoryOnHandValues;
  }

  @Selector()
  static currentInventoryTransactions(state: InventoryStateModel): ITransactionModel[] {
    return state.currentInventoryTransactions;
  }

  @Selector()
  static currentFilteredInventoryTransactions(state: InventoryStateModel): ITransactionModel[] {
    return state.currentFilteredInventoryTransactions;
  }

  @Selector()
  static isLibertyRxIntegrationConfigurationOn(state: InventoryStateModel): boolean {
      return state.isLibertyRxIntegrationConfigurationOn;
  }

  @Selector([PharmacyState.compoundingEnabled])
  static locations(state: InventoryStateModel, backCountEnabled: boolean): IInventoryLocation[] {
    if (backCountEnabled) {
      return state.locations;
    }
    return state.locations?.filter(x => x.type !== InventoryLocationTypeEnum.Compounds);
  }

  @Selector([PharmacyState.compoundingEnabled])
  static activeLocations(state: InventoryStateModel, backCountEnabled: boolean): IInventoryLocation[] {
    if (backCountEnabled) {
      return state.locations?.filter(x => x?.status === InventoryLocationStatusEnum.Active) || [];
    }
    return state.locations?.filter(x => x?.status === InventoryLocationStatusEnum.Active && x.type !== InventoryLocationTypeEnum.Compounds) || [];
  }


  @Selector()
  static primaryLocationID(state: InventoryStateModel): string {
      return state.locations?.find(x => x.type === InventoryLocationTypeEnum.Primary)?.id ?? null;
  }

  @Selector()
  static damagedExpiredLocationID(state: InventoryStateModel): string {
      return state.locations?.find(x => x.type === InventoryLocationTypeEnum.DamagedExpired)?.id ?? null;
  }

  @Selector()
  static currentInventoryForNdcOnLocation(state: InventoryStateModel): ICurrentInventoryOnLocation {
      return state.currentInventoryForNdcOnLocation;
  }

  @Selector()
  static bulkInventoryForNdcOnLocation(state: InventoryStateModel): ICurrentInventoryOnLocation[] {
      return state.bulkInventoryForNdcOnLocation;
  }

  @Selector()
  static similarOnHand(state: InventoryStateModel): ISimilarOnHand[] {
      return state.similarOnHand;
  }


  @Action(Act.ClearInventoryState)
  clearState(ctx: StateContext<InventoryStateModel>, {}: Act.ClearInventoryState): void {
    ctx.setState(null);
  }

  @Action(Act.SetCurrentFilteredInventoryTransactions)
  setCurrentFilteredInventoryTransactions(ctx: StateContext<InventoryStateModel>, {transactions}: Act.SetCurrentFilteredInventoryTransactions): void {
    ctx.patchState({currentFilteredInventoryTransactions: transactions || []});
  }


  @Action(Act.GetInventoryForDrug)
  getInventoryForDrug(ctx: StateContext<InventoryStateModel>, {drugID, locationID}: Act.GetInventoryForDrug): Observable<any> {
    ctx.patchState({loading: true, inventoryList: []});
    locationID??= '';
    return this.http.get(endpoints.inventory.getInventoryByDrugID + drugID + '/' + locationID)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              inventoryList: resp.value || [],
              receiveSelectedDrugInventory: resp?.value[0]
            });
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  // @Action(Act.GetInventory)
  // getInventory(ctx: StateContext<InventoryStateModel>, {transactionId}: Act.GetInventory): Observable<any> {
  //   ctx.patchState({loading: true, currentInventory: null});

  //   return this.http.get(endpoints.inventory.getInventory + transactionId)
  //     .pipe(
  //       tap((resp: any) => {
  //         ctx.patchState({
  //           currentInventory: resp?.value,
  //           loading: false,
  //         });
  //       })
  //     );
  // }

  @Action(Act.SetActiveInventoryList)
  setActiveInventoryList(ctx: StateContext<InventoryStateModel>, {list}: Act.SetActiveInventoryList): void {
    ctx.patchState({activeInventoryList: list || []});
  }

  @Action(Act.SetArchivedInventoryList)
  setArchivedInventoryList(ctx: StateContext<InventoryStateModel>, {list}: Act.SetArchivedInventoryList): void {
    ctx.patchState({archivedInventoryList: list || []});
  }

  @Action(Act.SetInventoryLocations)
  setInventoryLocations(ctx: StateContext<InventoryStateModel>, {locations}: Act.SetInventoryLocations): void {
    ctx.patchState({locations: locations || []});
  }

  @Action(Act.GetInventoryList)
  getInventoryList(ctx: StateContext<InventoryStateModel>, {filterData}: Act.GetInventoryList): Observable<IInventory[]> {
    ctx.patchState({loading: true, inventoryList: []});
    
    if(this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      filterData.schedules = !filterData?.schedules?.length ? this.drugService.getSchedulesList() : filterData?.schedules; 
    } else {
      filterData.schedules = filterData.schedules || [DrugScheduleEnum.C2];
    }

    return this.http.post(endpoints.inventory.getAll, {schedules: filterData.schedules})
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              inventoryList: resp.value.inventory,
              activeInventoryList: resp.value.inventory,
              archivedInventoryList: resp.value.inventory.filter(el => el.active === false)
            });
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetCurrentInventory)
  setInventory(ctx: StateContext<InventoryStateModel>, {inventory}: Act.SetCurrentInventory): void {
    ctx.patchState({currentInventory: inventory});
  }

  @Action(Act.SetCurrentInventoryFromDrugID)
  setInventoryByDrugID(ctx: StateContext<InventoryStateModel>, {drugID}: Act.SetCurrentInventoryFromDrugID): void {
    const state = ctx.getState();
    const inventoryResult = state.activeInventoryList?.find(i => i.drugID === drugID);
    if (inventoryResult) {
      ctx.patchState({currentInventory: inventoryResult});
    }
  }

  @Action(Act.ArchiveNDC)
  archiveNDC(ctx: StateContext<InventoryStateModel>, {inventory}: Act.ArchiveNDC): Observable<any> {
    return this.setDrugStatus(ctx, inventory.pharmacyID, inventory.drugID, !inventory.active);
  }

  @Action(Act.UnArchiveNDC)
  unArchiveNDC(ctx: StateContext<InventoryStateModel>, {inventory}: Act.UnArchiveNDC): Observable<any> {
    return this.setDrugStatus(ctx, inventory.pharmacyID, inventory.drugID, true);
  }

  private setDrugStatus(ctx: StateContext<InventoryStateModel>, pharmacyID: string, drugID: string, active: boolean):
    Observable<any> {
    ctx.patchState({loading: true});

    const result = {pharmacyID, drugID, status: active, success: false};

    return this.http.post(`${endpoints.inventory.setStatus}/${pharmacyID}/${drugID}?active=${active}`, null)
      .pipe(
        tap((resp: any) => {
          result.success = resp.success ?? false;
          ctx.patchState({
            archivingStatus: result,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false, archivingStatus: result});
          return of(null);
        }),
      );
  }

  @Action(Act.LoadInventoryFromLibertyRx)
  loadInventoryFromLibertyRx(ctx: StateContext<InventoryStateModel>, {}: Act.LoadInventoryFromLibertyRx): Observable<any[]> {
    ctx.patchState({
      loadingLibertyRxInventory: true, displayNoLibertySettingPopover: false,
      libertyRxLoadInventoryStatus: {
        totalPagesCount: 0, loadedPagesCount: 0, percentageCompleted: 0,
        c2Count: 0, hasErrors: false, status: 0, userAcknowledged: false
      },
      displayLoadLibertyRxInventoryCancelModal: false,
      libertyRxPolling: false
    });
    let url = endpoints.inventory.loadInventoryFromLibertyRx;
    return this.http.get(url)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            if(resp?.value?.libertyRxSettingsInactiveOrMissing) {
              ctx.patchState({loadingLibertyRxInventory: false, displayNoLibertySettingPopover : true});
            } else if (resp?.value?.failedToLoadFirstPageOrEnqueue) {
              ctx.patchState({loadingLibertyRxInventory: false, displayLoadLibertyRxInventoryErrorModal : true});
            } else {
              this.store.dispatch(new Act.GetLibertyRxLoadStatus());
            }
          }
        }),
        catchError((err) => {
          ctx.patchState({loadingLibertyRxInventory: false});
          return of(null);
        }),
      );
    //}
  }

  @Action(Act.CancelLoadInventoryFromLibertyRx)
  cancelLoadInventoryFromLibertyRx(ctx: StateContext<InventoryStateModel>, {}: Act.CancelLoadInventoryFromLibertyRx): Observable<any[]> {
    ctx.patchState({
      loadingLibertyRxInventory: false,
      displayNoLibertySettingPopover: false,
      //libertyRxLoadInventoryStatus: {totalPagesCount: 0, loadedPagesCount: 0, percentageCompleted: 0},
      libertyRxPolling: false
    });
    return this.http.get(endpoints.integration.liberty.cancelLoadInventoryFromLibertyRx)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({
              //libertyRxLoadInventoryStatus: {totalPagesCount: 0, loadedPagesCount: 0, percentageCompleted: 100},
              loadingLibertyRxInventory: false,
              libertyRxPolling: false
            });
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.GetInventoryOnHandForMoment)
  getInventoryOnHandForMoment(ctx: StateContext<InventoryStateModel>, {drugId, locationId, moment}: Act.GetInventoryOnHandForMoment): Observable<any> {
    ctx.patchState({loading: true});
    const payload = {drugId, locationId, moment}
    return this.http.post(endpoints.inventory.getInventoryOnHandForMoment, payload)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
            ctx.patchState({response : resp.value});
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ClearLoadedLibertyRxInventory)
  clearLoadedLibertyRxInventory(ctx: StateContext<InventoryStateModel>, {}: Act.ClearLoadedLibertyRxInventory): Observable<any> {
    return this.http.post(endpoints.integration.liberty.clearLibertyRxPageInventory, null)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            ctx.patchState({libertyRxLoadedInventory: []});
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.RemoveLoadedLibertyRxInventoryRow)
  removeLoadedLibertyRxInventoryRow(ctx: StateContext<InventoryStateModel>, {ndc}: Act.RemoveLoadedLibertyRxInventoryRow): Observable<any> {
    let ndcList = this.store.selectSnapshot(InventoryState.libertyRxLoadedInventory);
    const item = {
      drugId: ndc.id,
      friendlyName: ndc.name,
      ndc: ndc.packageNdc,
      onHandQty: ndc.adding
    };
    return this.http.post(endpoints.integration.liberty.removeLibertyRxPageInventory, item)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            if (!ndcList || !ndcList.length) {
              return;
            }

            const foundNdcIndex = ndcList.findIndex((value: INDC, i: number) => {
              return value.id == ndc.id;
            });

            if (foundNdcIndex >= 0)
            {
              ndcList.splice(foundNdcIndex, 1);
              ctx.patchState({libertyRxLoadedInventory: ndcList});
            }
            return resp.value;
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateLibertyRxInventoryRow)
  updateLoadedLibertyRxInventoryRow(ctx: StateContext<InventoryStateModel>, {ndc}: Act.UpdateLibertyRxInventoryRow): Observable<any> {
    const item = {
      drugId: ndc.id,
      friendlyName: ndc.name,
      ndc: ndc.packageNdc,
      onHandQty: ndc.adding
    };
    return this.http.post(endpoints.integration.liberty.updateLibertyRxPageInventory, item)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            return resp.value;
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.GetLibertyRxPageInventory)
  getLibertyRxPageInventory(ctx: StateContext<Array<any>>)
  : Observable<Array<any>> {
    return this.http.get(endpoints.integration.liberty.getLibertyRxPageInventory)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            var data = resp.value as Array<ILibertyRxLoadedInventory>;
            const rows = data.map((item: ILibertyRxLoadedInventory) =>
            {
              return {
                id: item?.drugId,
                name: item?.friendlyName,
                packageNdc: item?.ndc,
                adding: item?.onHandQty,
              } as any
            });
            this.store.dispatch(new Act.SaveLoadedLibertyRxInventory(resp.value));
            //ctx.patchState({libertyRxLoadedInventory: rows});
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.SaveLoadedLibertyRxInventory)
  saveLoadedLibertyRxInventory(ctx: StateContext<InventoryStateModel>, {data}: Act.SaveLoadedLibertyRxInventory): void {
    ctx.patchState(
      {
        libertyRxLoadedInventory: data?.map((item: ILibertyRxLoadedInventory) =>
        {
          return {
            id: item?.drugId,
            name: item?.friendlyName,
            packageNdc: item?.ndc,
            adding: item?.onHandQty,
          } as any
        })
      })
  }

  @Action(Act.GetLibertyRxLoadStatus)
  getPharmacyLibertyRxLoadStatus(ctx: StateContext<InventoryStateModel>)
    : Observable<ILibertyRxLoadInventoryStatus> {

    /*
      Potential conditions:
       1. Still loading - error modal hidden, no records modal hidden, pill spinner shown
            totalPagesCount > 0 && percentageCompleted < 100 && !hasErrors
       2. Loading failed - error modal shown, no records modal hidden, pill spinner hidden
            hasErrors
       3. Loading completed (with records) - error modal hidden, no records modal hidden, pill spinner hidden
            percentageCompleted == 100 && !hasErrors
       4. Loading completed (without records) - error modal hidden, no records modal shown, pill spinner hidden
            percentageCompleted == 100 && !hasErrors && c2Count > 0
       5. Awaiting acknowledgement
            percentageCompleted == 100 && !hasErrors && userAcknowledged is null
    */

    return this.http.get(endpoints.integration.liberty.getLibertyRxLoadStatus)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            var status = resp.value;

            const stillLoading = resp.value.status == LibertyRxLoadInventoryStatusValues.Processing
              || resp.value.status == LibertyRxLoadInventoryStatusValues.Created;
            const loadingFailed = resp.value.status == LibertyRxLoadInventoryStatusValues.Failed;
            const loadingCompletedNoRecords = resp.value.status == LibertyRxLoadInventoryStatusValues.Processed
              && status.percentageCompleted == 100 && status.c2Count == 0;
            const loadingCompletedRecordsFound = resp.value.status == LibertyRxLoadInventoryStatusValues.Processed
              && status.percentageCompleted == 100 && status.c2Count > 0;
            const nothingToDo = status.userAcknowledged || status.status == LibertyRxLoadInventoryStatusValues.NotLoading;
            // const stillPolling = status.value.status == LibertyRxLoadInventoryStatusValues.Created ||
            //   status.value.status == LibertyRxLoadInventoryStatusValues.Processing;

            var statusObj = {
              stillLoading: stillLoading,
              loadingFailed: loadingFailed,
              loadingCompletedNoRecords: loadingCompletedNoRecords,
              loadingCompletedRecordsFound: loadingCompletedRecordsFound,
              nothingToDo: nothingToDo,

              displayLoadLibertyRxInventoryCancelModal: false,
              displayLoadLibertyRxInventoryErrorModal: false,
              displayNoNewNdcPopover: false,
              displayNoLibertySettingPopover: false,
              loadingLibertyRxInventory: false,
              stillPolling: status.status == LibertyRxLoadInventoryStatusValues.Created ||
                status.status == LibertyRxLoadInventoryStatusValues.Processing
            };

            if (loadingFailed) {
              statusObj.loadingLibertyRxInventory = false;
              statusObj.displayLoadLibertyRxInventoryCancelModal = false;
              statusObj.displayLoadLibertyRxInventoryErrorModal = !status.userAcknowledged;
              statusObj.displayNoNewNdcPopover = false;
            } else if (loadingCompletedNoRecords) {
              statusObj.loadingLibertyRxInventory = false;
              statusObj.displayLoadLibertyRxInventoryCancelModal = false;
              statusObj.displayLoadLibertyRxInventoryErrorModal = false;
              statusObj.displayNoNewNdcPopover = !status.userAcknowledged;
            } else if (loadingCompletedRecordsFound) {
              statusObj.loadingLibertyRxInventory = false;
              statusObj.displayLoadLibertyRxInventoryCancelModal = false;
              statusObj.displayLoadLibertyRxInventoryErrorModal = false;
              statusObj.displayNoNewNdcPopover = false;
              this.store.dispatch(new Act.GetLibertyRxPageInventory());
            } else if (stillLoading) {
              statusObj.loadingLibertyRxInventory = true;
              statusObj.displayLoadLibertyRxInventoryCancelModal = false;
              statusObj.displayLoadLibertyRxInventoryErrorModal = false;
              statusObj.displayNoNewNdcPopover = false;
            } else if (nothingToDo) {
              statusObj.loadingLibertyRxInventory = false;
              statusObj.displayLoadLibertyRxInventoryCancelModal = false;
              statusObj.displayLoadLibertyRxInventoryErrorModal = false;
              statusObj.displayNoNewNdcPopover = false;
            }

            ctx.patchState({
              libertyRxLoadInventoryStatus: resp.value,
              libertyRxLoadInventoryPercentage: resp.percentageCompleted,
              loadingLibertyRxInventory: statusObj.loadingLibertyRxInventory,
              displayLoadLibertyRxInventoryCancelModal: statusObj.displayLoadLibertyRxInventoryCancelModal,
              displayLoadLibertyRxInventoryErrorModal: statusObj.displayLoadLibertyRxInventoryErrorModal,
              displayNoNewNdcPopover: statusObj.displayNoNewNdcPopover,
              libertyRxPolling: statusObj.stillPolling
            });
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.SetUserAcknowledgedLibertyRxLoadStatus)
  setUserAcknowledgedLibertyRxLoadStatus(ctx: StateContext<InventoryStateModel>, {}:
    Act.SetUserAcknowledgedLibertyRxLoadStatus): Observable<ILibertyRxLoadInventoryStatus> {
    return this.http.post(endpoints.integration.liberty.postLibertyRxLoadAcknowledged, null)
      .pipe(
        tap((resp: any) => {
          if (resp?.success) {
            ctx.patchState({libertyRxPolling: false, libertyRxLoadInventoryStatus: resp.value});
            return resp.value;
          }
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }


  // toggles the spinning pill loading indicator
  @Action(Act.DisplayLoadingLibertyRxInventory)
  setLoadingLibertyRxInventory(ctx: StateContext<InventoryStateModel>, {value}: Act.DisplayLoadingLibertyRxInventory): void {
    ctx.patchState({loadingLibertyRxInventory: value});
  }

  // toggles the modal that says we need to setup the LibertyRx integration
  @Action(Act.DisplayNoLibertySettingPopover)
  displayNoLibertySettingPopover(ctx: StateContext<InventoryStateModel>, {value}: Act.DisplayNoLibertySettingPopover): void {
    ctx.patchState({displayNoLibertySettingPopover: value});
  }

  // toggles the modal that says we didn't get anything from LibertyRx
  @Action(Act.DisplayNoNewNdcPopover)
  displayNoNewNdcPopover(ctx: StateContext<InventoryStateModel>, {value}: Act.DisplayNoNewNdcPopover): void {
    ctx.patchState({displayNoNewNdcPopover: value});
  }

  // toggles the modal that says we encountered an error during the LibertyRx load process
  @Action(Act.DisplayLoadLibertyRxInventoryErrorModal)
  displayLoadLibertyRxInventoryErrorModal(ctx: StateContext<InventoryStateModel>,
    {display}: Act.DisplayLoadLibertyRxInventoryErrorModal): void {
    ctx.patchState({displayLoadLibertyRxInventoryErrorModal: display});
  }

  // toggles the modal that says the load process was cancelled (currently unused)
  @Action(Act.DisplayLoadLibertyRxInventoryCancelModal)
  displayLoadLibertyRxInventoryCancelModal(ctx: StateContext<InventoryStateModel>,
    {display}: Act.DisplayLoadLibertyRxInventoryCancelModal): void {
    ctx.patchState({displayLoadLibertyRxInventoryCancelModal: display});
  }

  @Action(Act.ValidateTransactionDateForInventories)
  validateTransactionDateForInventories(ctx: StateContext<InventoryStateModel>, {payload}:
  Act.ValidateTransactionDateForInventories): Observable<IResponse> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.transaction.validateTransactionDateForInventories, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          ctx.patchState({
            loading: false
          });
          return of(null);
        }),
      );
  }

  @Action(Act.ValidateTransactionDateForAmend)
  validateTransactionDateForAmend(ctx: StateContext<InventoryStateModel>, {payload}:
  Act.ValidateTransactionDateForAmend): Observable<IResponse> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.transaction.ValidateTransactionDateForAmend, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.ValidateTransactionDateForCompoundAmendIngredients)
  validateTransactionDateForCompoundAmendIngredients(ctx: StateContext<InventoryStateModel>, {payload}:
  Act.ValidateTransactionDateForCompoundAmendIngredients): Observable<IResponse> {
    ctx.patchState({loading: true, response: null});
    return this.http.post(endpoints.transaction.ValidateTransactionDateForCompoundAmendIngredients, payload)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            response: resp
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.GetCurrentInventoryTransactions)
  getCurrentInventoryTransactions(ctx: StateContext<InventoryStateModel>, {drugId}:
  Act.GetCurrentInventoryTransactions): Observable<IResponse> {
    ctx.patchState({loading: true, currentInventoryTransactions: []});
    return this.http.get(endpoints.inventory.getInventoryTransactions + drugId)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            currentInventoryTransactions: this.transactionService.mapTransactions(resp.value || [])
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
    }

  @Action(Act.IsLibertyRxIntegrationConfigurationOn)
  isLibertyRxIntegrationConfigurationOn(ctx: StateContext<InventoryStateModel>, { }:
      Act.IsLibertyRxIntegrationConfigurationOn): Observable<IResponse> {
      ctx.patchState({ loading: true, isLibertyRxIntegrationConfigurationOn: false });
      return this.http.get(endpoints.integration.liberty.isConfigurationOn)
          .pipe(
              tap((resp: any) => {
                  ctx.patchState({
                      loading: false,
                      isLibertyRxIntegrationConfigurationOn: resp?.value
                  });
              }),
              catchError((err) => {
                  return of(null);
              }),
          );
  }

  @Action(Act.AddOrUpdateLocation)
  addOrUpdateLocation(ctx: StateContext<InventoryStateModel>,
    {location}: Act.AddOrUpdateLocation): void {
      let locations = [...ctx.getState().locations] || [];
      const existingLocationIdx = locations.findIndex(x => x.id === location.id);
      if (existingLocationIdx > -1) {
        locations = locations.map((x, index) => index === existingLocationIdx ? 
          {...x, 
            name: location?.name,
            description: location?.description,
            type: InventoryLocationTypeEnum.Custom
          } : x)
        }
      else {
        locations.push(location);
      }
      ctx.patchState({locations});
  }

  @Action(Act.GetAllLocations)
  getAllLocations(ctx: StateContext<InventoryStateModel>, {}:
      Act.GetAllLocations): Observable<IResponse> {
      ctx.patchState({ loading: true });
      return this.http.get(endpoints.inventoryLocation.getAll)
          .pipe(
              tap((resp: any) => {
                if (resp?.success) {
                  ctx.patchState({
                    locations: resp?.value
                });
                }
                ctx.patchState({
                  loading: false
                });
              }),
              catchError((err) => {
                  return of(null);
              }),
          );
  }

  @Action(Act.RemoveLocation)
  removeLocation(ctx: StateContext<InventoryStateModel>, {id}:
      Act.RemoveLocation): void {
        let locations = ([...ctx.getState().locations] || []).filter(x => x.id !== id);
        ctx.patchState({locations});
  }

  @Action(Act.MarkLocationAsIrremovable)
  markLocationAsIrremovable(ctx: StateContext<InventoryStateModel>, {id}:
      Act.MarkLocationAsIrremovable): void {
        ctx.patchState({locations: [...ctx.getState().locations.map(x => x.id === id ? ({...x, canDelete: false}) : x)]});
  }

  @Action(Act.UpdateLocationActivity)
  updateLocationActivity(ctx: StateContext<InventoryStateModel>, {id, isActive}:
      Act.UpdateLocationActivity): void {
        ctx.patchState({locations: [...ctx.getState().locations
          .map(x => x.id === id ? (
            {...x, 
              status:  isActive ? InventoryLocationStatusEnum.Active : InventoryLocationStatusEnum.Inactive
            }) : x)]});
  }

  @Action(Act.GetInventoryOnLocation)
  getInventoryOnLocation(ctx: StateContext<InventoryStateModel>, {id, drugID, gpi}:
      Act.GetInventoryOnLocation): Observable<IResponse> {
      ctx.patchState({ loading: true, currentInventoryForNdcOnLocation: null });
      const url = endpoints.inventoryLocation.inventoryOnLocation + id + '/' + drugID + '/' + (!!gpi ? gpi : '');
      return this.http.get(url)
          .pipe(
              tap((resp: any) => {
                if (resp?.success) {
                    ctx.patchState({
                      currentInventoryForNdcOnLocation: { locationID: id, drugID: drugID, onHand: resp?.value?.onHand, similarOnHand: resp?.value?.similarOnHand }
                  });
                }
                ctx.patchState({
                  loading: false
                });
              }),
              catchError((err) => {
                  return of(null);
              }),
          );
  }

  @Action(Act.BulkGetInventoryOnLocation)
  bulkGetInventoryOnLocation(ctx: StateContext<InventoryStateModel>, {locationDrugList}:
      Act.BulkGetInventoryOnLocation): Observable<void> {
      ctx.patchState({ loading: true, bulkInventoryForNdcOnLocation: [] });
      return this.http.post(endpoints.inventoryLocation.bulkGetInventoryOnLocation, locationDrugList)
          .pipe(
              tap((resp: any) => {
                if (resp?.success) {
                  ctx.patchState({
                    bulkInventoryForNdcOnLocation: resp?.value
                  });
                }
                ctx.patchState({
                  loading: false
                });
              }),
              catchError((err) => {
                  return of(null);
              }),
          );
  }

  @Action(Act.MoveInventory)
  moveInventory(ctx: StateContext<InventoryStateModel>, {payload}:
      Act.MoveInventory): Observable<void> {
      ctx.patchState({ loading: true, success: null});
      return this.http.post(endpoints.inventory.move, payload)
          .pipe(
              tap((resp: any) => {
                if (resp?.success) {
                    ctx.patchState({
                      success: true
                  });
                }
                ctx.patchState({
                  loading: false
                });
              }),
              catchError((err) => {
                  return of(null);
              }),
          );
  }


  @Action(Act.BulkGetSimilarOnHandInventory)
  bulkGetSimilarOnHandInventory(ctx: StateContext<InventoryStateModel>, {gpiArray}: Act.BulkGetSimilarOnHandInventory): Observable<void> {
      ctx.patchState({ loading: true, success: null, similarOnHand: []});
      let params = new HttpParams();
      gpiArray.forEach(gpi => {
        params = params.append('gpi', gpi);
      });
      return this.http.get(endpoints.inventory.bulkGetSimilar, {params})
          .pipe(
              tap((resp: any) => {
                if (resp?.success) {
                    ctx.patchState({
                      success: true,
                      loading: false,
                      similarOnHand: resp?.value
                  });
                }
                ctx.patchState({
                  loading: false
                });
              }),
              catchError((err) => {
                ctx.patchState({
                  loading: false
                });
                  return of(null);
              }),
          );
  }
}
